import type React from 'react'
import { useEffect } from 'react'
import { CircleHelp } from 'lucide-react'
import { useLocalStorage } from '@uidotdev/usehooks'

import { Alert, AlertDescription, AlertTitle } from 'shadcdn/ui/alert'
import { Button } from 'ui/Button/Button'
import { useAuthStore } from 'features/auth/useAuthStore'
import { trackPresyncEvent } from 'features/analytics/analytics'

import { InstalledSoftwareService } from './InstalledSoftwareService'
import { useAllowInstalledTracking } from './hooks'

export const useInstalledTrackingUserUuuid = () => InstalledSoftwareService.getUserUuid()

export const InstalledSoftwareTracker: React.FC = () => {
  // sends installed software data to the server
  // shows a permission alert if the user hasn't decided yet
  const userUuid = useInstalledTrackingUserUuuid()
  const [allowInstalledSoftwareTracking] = useAllowInstalledTracking()

  useEffect(() => {
    if (allowInstalledSoftwareTracking === 'anonymous') {
      InstalledSoftwareService.saveInstalledSoftware(userUuid)
    }
  }, [allowInstalledSoftwareTracking, userUuid])

  return <InstalledSoftwarePermissionAlert />
}

const InstalledSoftwarePermissionAlert: React.FC = () => {
  const [showAlert, setShowAlert] = useLocalStorage('INSTALLED_SOFTWARE_ALERT_SHOWN', true)

  useEffect(() => {
    if (showAlert) {
      trackPresyncEvent(trackPresyncEvent.events.INSTALLED_TRACKING_ALERT_SHOWN)
    }
  }, [showAlert])

  if (!showAlert || !window.api) return null

  const handleOkClick = () => {
    setShowAlert(false)
    InstalledSoftwareService.setAllowInstalledTracking('anonymous')
  }

  return (
    <Alert className="bg-white border-0 border-t rounded-none text-sm fixed z-[40] shadow-lg bottom-0">
      <CircleHelp className="h-4 w-4" />
      <div className="flex items-center">
        <div className="flex-grow">
          <AlertTitle>We are anonymously analyzing your installed plugins</AlertTitle>
          <AlertDescription className="text-xs leading-5">
            This helps us build an auto-update feature. No personal information is collected. You can opt-out on the
            settings page.
          </AlertDescription>
        </div>

        <div className="flex flex-row">
          <Button className="ml-4" color="primary" size="sm" onClick={handleOkClick}>
            OK
          </Button>
        </div>
      </div>
    </Alert>
  )
}

export const useInstalledSoftwareTelemetryEffect = () => {
  const authToken = useAuthStore((state) => state.authToken)

  useEffect(() => {
    if (!authToken) return
    InstalledSoftwareService.checkInstalledSoftware(authToken)
  }, [authToken])
}
